/**
 * Ported from hd_conf/src/lib/classes/DXFLoader.tsx
 * Original source: https://github.com/prolincur/three-dxf-loader
 * Includes inlined bspline.js and round10.js from same repo.
 *
 * Simplified for hd_neo: only SPLINE entities are processed; data URI support removed.
 */

import {
    BufferGeometry,
    FileLoader,
    Group,
    Line,
    LineBasicMaterial,
    Object3D,
    Points,
    Vector2,
    Vector3,
} from 'three';
import DxfParser, {
    IEntity,
    ISplineEntity,
    IPoint,
    IDxf,
} from 'dxf-parser';

export type DXFLoaderResultType = {
    dxf: IDxf;
    entities: (Line | Points | Object3D)[];
    points: Vector2[][];
};

class DXFLoader {
    enableLayer: boolean;
    splineInterpolationPerSegment: number;
    returnLinesAsPoints: boolean;

    constructor() {
        this.enableLayer = false;
        this.splineInterpolationPerSegment = 10;
        this.returnLinesAsPoints = true;
    }

    setEnableLayer(enableLayer: boolean) {
        this.enableLayer = enableLayer;
        return this;
    }

    setSplineInterpolationPerSegment(n: number) {
        this.splineInterpolationPerSegment = n;
        return this;
    }

    load({
        url,
        options,
        onLoad,
        onProgress,
        onError,
    }: {
        url: string;
        options?: { returnLinesAsPoints?: boolean };
        onLoad: (result: DXFLoaderResultType) => void;
        onProgress?: (event: ProgressEvent) => void;
        onError?: (event: ErrorEvent) => void;
    }) {
        if (options?.returnLinesAsPoints !== undefined) {
            this.returnLinesAsPoints = options.returnLinesAsPoints;
        }

        const loader = new FileLoader();
        loader.load(
            url,
            (text) => {
                try {
                    onLoad(this.parse(text as string));
                } catch (err) {
                    if (onError) onError(err as ErrorEvent);
                    else throw err;
                }
            },
            onProgress,
            onError as ((err: unknown) => void) | undefined,
        );
    }

    parse(text: string): DXFLoaderResultType {
        const parser = new DxfParser();
        const dxf = parser.parseSync(text) as IDxf;
        return this.loadEntities(dxf, this.enableLayer);
    }

    loadEntities(data: IDxf, enableLayer: boolean): DXFLoaderResultType {
        const splineInterpolationPerSegment = Math.floor(this.splineInterpolationPerSegment);
        const returnLinesAsPoints = this.returnLinesAsPoints;

        const entities: (Line | Points | Object3D)[] = [];
        const pointsArr: Vector2[][] = [];
        const layers: Record<string, Group> = {};

        for (const entity of data.entities) {
            const obj = drawEntity(entity);
            if (obj) {
                if (!(obj as Object3D).isObject3D) {
                    if (Array.isArray(obj)) {
                        pointsArr.push(obj as unknown as Vector2[]);
                    }
                } else {
                    entities.push(obj as Line | Points);
                    if (enableLayer && entity.layer) {
                        if (!layers[entity.layer]) {
                            layers[entity.layer] = new Group();
                            layers[entity.layer].name = entity.layer;
                        }
                        layers[entity.layer].add(obj as Line | Points);
                    }
                }
            }
        }

        return {
            entities: enableLayer ? Object.values(layers) : entities,
            dxf: data,
            points: pointsArr,
        };

        function drawEntity(entity: IEntity) {
            switch (entity.type) {
                case 'LINE':
                case 'POLYLINE':
                case 'LWPOLYLINE':
                case 'ARC':
                case 'POINT':
                case 'CIRCLE':
                case 'ELLIPSE':
                case 'DIMENSION':
                    return null;
                case 'SPLINE':
                    return drawSpline(entity as ISplineEntity, returnLinesAsPoints, splineInterpolationPerSegment);
                default:
                    return null;
            }
        }

        function drawSpline(entity: ISplineEntity, returnLineAsPoints: boolean, interpolationsPerSegment = 100) {
            const color = getEntityColor(entity);
            const points = getBSplinePolyline(
                entity.controlPoints as IPoint[],
                entity.degreeOfSplineCurve,
                entity.knotValues,
                interpolationsPerSegment,
            );

            if (returnLineAsPoints) {
                return points;
            } else {
                const geometry = new BufferGeometry().setFromPoints(points);
                const material = new LineBasicMaterial({ linewidth: 1, color });
                return new Line(geometry, material);
            }
        }

        function getEntityColor(entity: IEntity): number {
            let color = 0x000000;
            if (entity.color) color = entity.color;
            else if (data.tables?.layer?.layers[entity.layer]) {
                color = data.tables.layer.layers[entity.layer].color ?? 0x000000;
            }
            if (color === 0xffffff) color = 0x000000;
            return color;
        }
    }
}

export default DXFLoader;

// ─── B-Spline implementation (inlined from https://github.com/thibauts/b-spline) ───

function bSpline(t: number, degree: number, points: number[][], knots: number[], weights?: number[]): number[] {
    const n = points.length;
    const d = points[0].length;

    if (t < 0 || t > 1) throw new Error(`t out of bounds [0,1]: ${t}`);
    if (degree < 1) throw new Error('degree must be at least 1');
    if (degree > n - 1) throw new Error('degree must be <= point count - 1');

    if (!weights) {
        weights = new Array(n).fill(1) as number[];
    }

    if (!knots) {
        knots = Array.from({ length: n + degree + 1 }, (_, i) => i);
    } else if (knots.length !== n + degree + 1) {
        throw new Error('bad knot vector length');
    }

    const domain = [degree, knots.length - 1 - degree];
    const low = knots[domain[0]];
    const high = knots[domain[1]];
    t = Math.max(low, Math.min(high, t * (high - low) + low));

    let s = domain[0];
    for (; s < domain[1]; s++) {
        if (t >= knots[s] && t <= knots[s + 1]) break;
    }

    // Convert to homogeneous coordinates
    const v: number[][] = [];
    for (let i = 0; i < n; i++) {
        v[i] = [];
        for (let j = 0; j < d; j++) v[i][j] = points[i][j] * (weights as number[])[i];
        v[i][d] = (weights as number[])[i];
    }

    for (let l = 1; l <= degree + 1; l++) {
        for (let i = s; i > s - degree - 1 + l; i--) {
            const alpha = (t - knots[i]) / (knots[i + degree + 1 - l] - knots[i]);
            for (let j = 0; j < d + 1; j++) {
                v[i][j] = (1 - alpha) * v[i - 1][j] + alpha * v[i][j];
            }
        }
    }

    const result: number[] = [];
    for (let i = 0; i < d; i++) result[i] = round10(v[s][i] / v[s][d], -9);
    return result;
}

function getBSplinePolyline(
    controlPoints: IPoint[],
    degree: number,
    knots: number[],
    interpolationsPerSplineSegment: number,
    weights?: number[],
): Vector3[] {
    const polyline: Vector3[] = [];
    const pts = controlPoints.map((p) => [p.x, p.y, p.z]);

    const segmentTs = [knots[degree]];
    const domain = [knots[degree], knots[knots.length - 1 - degree]];

    for (let k = degree + 1; k < knots.length - degree; k++) {
        if (segmentTs[segmentTs.length - 1] !== knots[k]) segmentTs.push(knots[k]);
    }

    interpolationsPerSplineSegment = interpolationsPerSplineSegment || 25;
    for (let i = 1; i < segmentTs.length; i++) {
        const uMin = segmentTs[i - 1];
        const uMax = segmentTs[i];
        for (let k = 0; k <= interpolationsPerSplineSegment; k++) {
            const u = (k / interpolationsPerSplineSegment) * (uMax - uMin) + uMin;
            let t = (u - domain[0]) / (domain[1] - domain[0]);
            t = Math.max(0, Math.min(1, t));
            const p = bSpline(t, degree, pts, knots, weights);
            polyline.push(new Vector3(p[0], p[1], p[2]));
        }
    }
    return polyline;
}

// ─── round10.js (inlined) ───

function round10(value: number, exp: number): number {
    if (+exp === 0) return Math.round(value);
    const v = +value;
    const e = +exp;
    if (isNaN(v) || e % 1 !== 0) return NaN;
    const [significand, exponent] = `${v}e`.split('e');
    const rounded = Math.round(+`${significand}e${+exponent - e}`);
    const [sig2, exp2] = `${rounded}e`.split('e');
    return +`${sig2}e${+exp2 + e}`;
}
